import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import type { AppState, AppDispatch } from '@nextgen-web-framework/core';
import { Logger } from '@nextgen-web-framework/core';
import {
  useTranslation,
  Input,
  InputTypes,
  MLPVersion,
  InputValidations,
  Theme,
  IconStyle,
  Shade,
  FAIcon,
  Drawer,
  ButtonSize,
  Button,
  ButtonStyles,
  ButtonTypes,
  ButtonWidth,
  AccountType,
} from '@nextgen-web-framework/all';
import {
  SponsorSearchProps,
  profileApi,
  updateRegistrationData,
  SponsorSearchResponseInner,
} from '@commerce-profile-webcomponents/sdk';
import logObject from '../../../../logObject';
import { SponsorCard } from '../SponsorCard/SponsorCard';
import { InternationalSponsor } from '../InternationalSponsor/InternationalSponsor';
import styles from './SelectSponsor.module.scss';
import { DrawerProps, SelectSponsorProps } from './types/propTypes';

const SelectSponsor = ({
  isIdVerified,
  showInternationalSponsor,
  showInsideModal,
  isFocused = false,
}: SelectSponsorProps) => {
  const logger = Logger.getInstance(logObject('SelectSponsor.tsx', 'SelectSponsor'));
  const [isDrawerOpen, setIsDrawerOpen] = useState(false); //internationalSponsorDrawer (for I know ABO section)
  const [hideDropDownLabel, setHideDropDownLabel] = useState(false);
  const selectedAccountType: AccountType = useSelector((state: AppState) => state.commerceProfile.selectedAccountType);
  const { selectSponsor } = useSelector((state: AppState) => state.commerceProfile.registrationFlowData);
  const {
    internationalSponsor,
    sponsor,
    internationalSponsorTncCheckbox,
    editSponsor,
    setInternationalSponsor,
    internationalSposnorVerified,
  } = selectSponsor;
  const t = useTranslation();
  const { BusinessOwner } = AccountType;
  const dispatch = useDispatch<AppDispatch>();
  const [sponsorTxt, setSponsorTxt] = useState<string>('');

  const [sponsorLocal, setSponsorLocal] = useState<SponsorSearchResponseInner | null>({});
  const [knowABO, setKnowABO] = useState(true);
  const [verificationStatus, setVerificationStatus] = useState<InputValidations>(InputValidations.DEFAULT);
  const sponsorExist = sponsor.accountNumber;
  const sponsorLocalExist = Object.keys(sponsorLocal ?? '').length !== 0;
  const internationalSponsorExist = internationalSponsor.accountNumber;
  const [showLoadingComp, setShowLoadingComp] = useState(false);

  const handleSponsorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!showLoadingComp) {
      setSponsorTxt(e.target.value);
      setVerificationStatus(InputValidations.DEFAULT);
    }
  };

  useEffect(() => {
    setSponsorLocal(sponsor);
  }, [sponsor]);
  const updateAbo = (abo: SponsorSearchResponseInner | null) => {
    setSponsorTxt('');
    dispatch(
      updateRegistrationData({
        selectSponsor: {
          ...selectSponsor,
          sponsor: abo,
        },
      }),
    );
  };
  const onEnterSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onSponsorVerifyClick();
    }
  };
  const onSponsorVerifyClick = async () => {
    setShowLoadingComp(true);
    try {
      dispatch(
        updateRegistrationData({
          selectSponsor: {
            ...selectSponsor,
            sponsorSearchText: sponsorTxt,
            knowABO: true,
          },
        }),
      );
      const payload: SponsorSearchProps = {
        sponsorInputValue: sponsorTxt,
        accountType: selectedAccountType,
      };

      await profileApi
        .getSponsorByAccountNumber({
          ...payload,
        })
        .then((res: SponsorSearchResponseInner[]) => {
          setShowLoadingComp(false);
          updateAbo(res[0]);

          setVerificationStatus(InputValidations.DEFAULT);
        })
        .catch(() => {
          setShowLoadingComp(false);
          setVerificationStatus(InputValidations.INVALID);
        });
    } catch (err) {
      logger.error(err);
    }
  };
  const handleSelectwithABO = () => {
    updateAbo({});
    setSponsorTxt('');
    setHideDropDownLabel(false);
    dispatch(
      updateRegistrationData({
        selectSponsor: {
          ...selectSponsor,
          internationalSponsor: {},
          internationalSposnorVerified: false,
          internationalSponsorCountryCode: '',
          sponsor: {},
          isInternational: false,
          internationalSponsorType: '',
          internationalSponsorTncCheckbox: false,
          setInternationalSponsor: false,
        },
      }),
    );
  };

  const openDrawer = () => {
    sponsorExist && setIsDrawerOpen(true); //only open international when sponsor is existing.
    setSponsorTxt('');
  };

  const drawerCloseHandler = () => {
    setIsDrawerOpen(false);
    setSponsorTxt('');
    if (!sponsorLocalExist) {
      setKnowABO(true);
    }
    //when drawer was closed and internationalSponsor was not set in redux, then clear drawer inputs.
    if (!setInternationalSponsor) {
      dispatch(
        updateRegistrationData({
          selectSponsor: {
            ...selectSponsor,
            isInternational: false,
            internationalSponsor: {},
            internationalSposnorVerified: false,
            internationalSponsorCountryCode: '',
            internationalSponsorType: '',
            internationalSponsorTncCheckbox: false,
          },
        }),
      );
    }
  };
  let enableAddButton = true;
  if (internationalSponsorTncCheckbox) {
    enableAddButton = !internationalSposnorVerified;
  } else {
    enableAddButton = true;
  }
  const internationalSponsorDrawer = () => (selectedAccountType === BusinessOwner ? <InternationalSponsor /> : <></>);
  const internaltionalABOHeading = t('profile.SinglePageRegistrationStep.internaltionalSponsor');
  const drawerHandler = ({ heading, displayComponent }: DrawerProps) => (
    <Drawer
      header={heading}
      data-testid="abo-drawer-test-id"
      closeIcon
      className="sponsor-drawer"
      onClose={drawerCloseHandler}
      bottomGap={0}
      footer={
        sponsorExist && (
          <div className={styles['add-btn']}>
            <Button
              title={t('profile.RegistrationSponsor.addButton')}
              type={ButtonTypes.SUBMIT}
              data-testid="international-sponsor-add-button-testid"
              buttonStyle={ButtonStyles.SOLID}
              size={ButtonSize.MEDIUM}
              disabled={enableAddButton}
              onClick={addHandler}
              buttonWidth={ButtonWidth.FULL_WIDTH}
            />
          </div>
        )
      }
    >
      <div className={styles['sponsor-drawer']}>{displayComponent && displayComponent()}</div>
    </Drawer>
  );

  const sponsorInputField = () => (
    <div className={styles['sponsor-field']}>
      <>
        <Input
          id="sponsor"
          data-testid="sponsor"
          type={InputTypes.NUMBER}
          name="sponsor"
          value={sponsorTxt}
          label={t('profile.SelectSponsor.sponsor')}
          onChange={handleSponsorChange}
          maxLength={50}
          mlpVersion={MLPVersion.TWO}
          className={`${sponsorTxt ? styles['verify-link'] : ''}`}
          status={sponsorTxt ? verificationStatus : InputValidations.DEFAULT}
          onKeyDown={onEnterSearch}
          disabled={isIdVerified}
          // eslint-disable-next-line
          autoFocus={isFocused}
          message={
            verificationStatus === InputValidations.INVALID && knowABO
              ? t('profile.SinglePageRegistrationStep.ABOinputError')
              : ''
          }
          theme={verificationStatus === InputValidations.INVALID ? Theme.ERROR : Theme.BLACK}
          inputIcon={showLoadingComp ? { iconName: 'spinner-third', iconStyle: IconStyle.SOLID } : undefined}
          iconClassName="fa-spin"
          ctaTextLink={{
            text: !showLoadingComp ? t('profile.SinglePageRegistrationStep.verify') : '',
            id: 'btn_verify',
            onClick: onSponsorVerifyClick,
            disabled: sponsorTxt ? false : true,
          }}
        />
      </>
    </div>
  );
  const handlerInternationlSponsorCrossButton = () => {
    setHideDropDownLabel(false);
    dispatch(
      updateRegistrationData({
        selectSponsor: {
          ...selectSponsor,
          internationalSponsor: {},
          internationalSposnorVerified: false,
          internationalSponsorCountryCode: '',
          isInternational: false,
          internationalSponsorType: '',
          internationalSponsorTncCheckbox: false,
          setInternationalSponsor: false,
        },
      }),
    );
  };

  const isSponsor = sponsorLocalExist || sponsorExist;
  const addHandler = () => {
    setIsDrawerOpen(false);

    dispatch(
      updateRegistrationData({
        selectSponsor: {
          ...selectSponsor,
          setInternationalSponsor: true,
        },
      }),
    );
    setHideDropDownLabel(true);
  };

  // Fix for Sonar - Cognitive Complexity
  const renderSponsorCard = () => {
    if (isSponsor) {
      let aboNum = sponsor;
      if (!sponsorExist) {
        aboNum = sponsorLocal;
      }
      let sponsorCardStyle = styles['sponsor-card-container'];
      if (setInternationalSponsor) {
        sponsorCardStyle += ` ${styles['width']}`;
      }
      return (
        <div className={sponsorCardStyle} data-testid="sponsor-card-container">
          {!showInsideModal && (
            <div className={styles['sponsor-heading']} data-testid="sponsor-heading">
              {t('profile.SelectSponsor.sponsorHeading')}
            </div>
          )}
          <SponsorCard abo={aboNum} addHandler={handleSelectwithABO} isSelected={true} isDisplay={!editSponsor} />
        </div>
      );
    } else {
      return sponsorInputField();
    }
  };

  const sponsorLabel = `${t('profile.SinglePageRegistrationStep.internaltionalSponsor')}`;
  // Fix for Sonar - Cognitive Complexity
  const showIntSponsorHeading = selectedAccountType === BusinessOwner && !hideDropDownLabel;
  const showInternationalSponsorSection = showInternationalSponsor && isSponsor && !showInsideModal;

  const renderInternationalSponsorSection = () => {
    if (isDrawerOpen) {
      const drawerprops = { heading: internaltionalABOHeading, displayComponent: internationalSponsorDrawer };
      return drawerHandler(drawerprops);
    } else if (selectedAccountType === BusinessOwner && internationalSponsorExist && setInternationalSponsor) {
      const headingStyle = `${styles['int-sponsor-card-heading']} ${styles['remove-padding-top']}`;
      // Comment: Based on the logic, this will be always true
      // Original code: https://github.com/AmwayCommon/commerce-profile-webcomponents/pull/489/commits/d996fc5c3742e127023c218fc7538e7aadbe76e5#diff-e8a81352f1ecc4a8a96270fd9b56a888c20fe9c5dd1c80b1a6ec630658753db7
      // Original Logic: if (internationalSponsorExist) headingStyle += ` ${styles['remove-padding-top']}`;

      return (
        <div className={styles['int-sponsor-card']}>
          <div className={headingStyle}>{t('profile.RegistrationSponsor.internationalSponsorHeading')}</div>
          <SponsorCard
            abo={internationalSponsor}
            addHandler={handlerInternationlSponsorCrossButton}
            isSelected={true}
            isInternationalABO={false}
          />
        </div>
      );
    } else {
      // Comment: Is this valid? (the if is checking isSponsor, it will not render for this part)
      // Original code: https://github.com/AmwayCommon/commerce-profile-webcomponents/pull/489/commits/d996fc5c3742e127023c218fc7538e7aadbe76e5#diff-e8a81352f1ecc4a8a96270fd9b56a888c20fe9c5dd1c80b1a6ec630658753db7
      // Original Logic: if (!isSponsor) sponsorLabel = t('profile.SelectSponsor.dontknowABO');

      return <></>;
    }
  };

  let containerStyle = styles['sponsor-container'];
  let internationalContainerStyle = styles['sponsor-dropdown'];
  if (setInternationalSponsor) {
    containerStyle += ` ${styles['flex']}`;
  }
  if (internationalSponsorExist && hideDropDownLabel) {
    internationalContainerStyle += ` ${styles['remove-padding']}`;
  }
  return (
    <div className={containerStyle} data-testid="sponsor-container">
      {renderSponsorCard()}
      {showInternationalSponsorSection && (
        <div className={internationalContainerStyle} data-testid="sponsor-dropdown">
          {showIntSponsorHeading && (
            <div
              className={styles['sponsor-dropdown-container']}
              data-testid="sponsor-dropdown-container"
              onClick={() => {
                if (isIdVerified) {
                  return;
                }
                openDrawer();
              }}
              tabIndex={-1}
              aria-hidden="true"
            >
              <div className={styles['sponsor-dropdown-text']}>{sponsorLabel}</div>
              <div className={styles['sponsor-dropdown-icon']}>
                <FAIcon
                  theme={Theme.BLACK}
                  iconName={'chevron-down'}
                  shade={Shade.DARK}
                  iconStyle={IconStyle.SOLID}
                  data-testid="icon-tesid"
                />
              </div>
            </div>
          )}
          {renderInternationalSponsorSection()}
        </div>
      )}
    </div>
  );
};

export default SelectSponsor;
